/**
 * PhysicalSummaryStep — Step 2 of the physical checkout variant.
 *
 * Renders:
 *   - Order summary card (product title + variation + seller + quantity +
 *     unit price + subtotal + fulfillment method + shipping cost +
 *     grand total).
 *   - Conditional fulfillment summary card (ship → address block;
 *     pickup → location block).
 *   - Escrow explanation paragraph (24h auto-close + 72h seller timeout).
 *   - Inline error alert + footer (Atrás → Step 1 / Continuar → Step 3).
 *
 * Founder directive (2026-11): 2FA removed from checkout — no
 * marketplace requires 2FA to buy. The "Verificar 2FA" button is now a
 * "Continuar" button that transitions directly to the verify step.
 * The TwoFaActivationRequiredCard (2FA enrollment gate) was also
 * removed.
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original Step 2 JSX block (lines 1483-1626 of
 * the monolith).
 *
 * Anti-P-041: 0 `as any`. Anti-P-042: 0 `console.*`.
 * Anti-P-366: only adds the 2FA pre-check (Bug #2 fix).
 */

import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Truck, MapPin, ArrowLeft, ArrowRight } from 'lucide-react';
import type {
  PublicProductResponse,
  FulfillmentMethod,
  ShippingAddress,
  VariationSelection,
} from '@profileia/shared';
import { formatCredits } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Estimate {
  unitPrice: number;
  totalPrice: number;
  shippingCost: number;
  grandTotal: number;
  freeShippingApplied: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface PhysicalSummaryStepProps {
  product: PublicProductResponse;
  variationSelection: VariationSelection | null;
  quantity: number;
  fulfillmentMethod: FulfillmentMethod | null;
  shippingAddress: ShippingAddress;
  estimate: Estimate;
  pickupLoc: { lat: number; lng: number; address: string } | null;
  submitting: boolean;
  submitError: string | null;
  onBack: () => void;
  /** Transitions to the verify step (Founder directive 2026-11: 2FA
   * removed — this used to open the 2FA dialog, now it just advances). */
  onContinue: () => void;
  onClose: () => void;
}

export function PhysicalSummaryStep({
  product,
  variationSelection,
  quantity,
  fulfillmentMethod,
  shippingAddress,
  estimate,
  pickupLoc,
  submitting,
  submitError,
  onBack,
  onContinue,
  onClose,
}: PhysicalSummaryStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <p className="text-sm font-medium line-clamp-2">
              {product.title}
            </p>
            {variationSelection && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {variationSelection.name}: <span className="font-medium">{variationSelection.option}</span>
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-0.5">
              Vendido por <span className="font-medium">{product.seller.storeName}</span>
            </p>
          </div>
        </div>
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Cantidad</dt>
            <dd className="font-medium tabular-nums">{quantity}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Precio unitario</dt>
            <dd className="font-medium tabular-nums">
              {formatCredits(estimate.unitPrice)} créditos
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="font-medium tabular-nums">
              {formatCredits(estimate.totalPrice)} créditos
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Método de entrega</dt>
            <dd className="font-medium">
              {fulfillmentMethod === 'ship' ? 'Envío a domicilio' : 'Recogida en local'}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Costo de envío</dt>
            <dd className="font-medium tabular-nums">
              {fulfillmentMethod === 'pickup' ? (
                '—'
              ) : estimate.freeShippingApplied ? (
                <span className="text-emerald-600 dark:text-emerald-400">
                  Gratis (umbral alcanzado)
                </span>
              ) : (
                `${formatCredits(estimate.shippingCost)} créditos`
              )}
            </dd>
          </div>
          <div className="border-t border-border pt-2 flex justify-between text-base">
            <dt className="font-semibold">Total estimado</dt>
            <dd className="font-bold tabular-nums">
              {formatCredits(estimate.grandTotal)} créditos
            </dd>
          </div>
        </dl>
      </div>

      {/* Fulfillment summary */}
      {fulfillmentMethod === 'ship' && (
        <div className="rounded-md border border-border p-3 text-sm">
          <p className="font-medium flex items-center gap-1.5 mb-1">
            <Truck className="size-4" aria-hidden="true" />
            Enviar a
          </p>
          <address className="not-italic text-muted-foreground text-xs">
            {shippingAddress.name}<br />
            {shippingAddress.address}<br />
            {shippingAddress.city}, {shippingAddress.country}{' '}
            {shippingAddress.zip}<br />
            Tel: {shippingAddress.phone}
          </address>
        </div>
      )}
      {fulfillmentMethod === 'pickup' && pickupLoc && (
        <div className="rounded-md border border-border p-3 text-sm">
          <p className="font-medium flex items-center gap-1.5 mb-1">
            <MapPin className="size-4" aria-hidden="true" />
            Recoger en
          </p>
          <address className="not-italic text-muted-foreground text-xs">
            {pickupLoc.address}
          </address>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Al confirmar, tus créditos se debitarán a una cuenta de
        <strong> escrow</strong> (no se transfieren al vendedor hasta
        que confirmes recepción o pasen 24h tras la entrega). El total
        final lo calcula el servidor y puede diferir ligeramente si el
        vendedor actualizó el costo de envío.
      </p>

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-col gap-2 border-t border-border bg-background px-6 py-3 sm:flex-col">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
          disabled={submitting}
        >
          <ArrowLeft className="size-4 mr-1" aria-hidden="true" />
          Atrás
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={onContinue}
          disabled={submitting}
        >
          Continuar
          <ArrowRight className="size-4 ml-1" aria-hidden="true" />
        </Button>
      </DialogFooter>
    </div>
  );
}
